// Create a context
import React, { createContext, ReactElement, useEffect, useState } from "react";
import { Auth } from "aws-amplify";
import { CognitoUser } from "amazon-cognito-identity-js";
import { message } from "antd";

import { Restaurant, RestaurantStatus } from "@tasty/api";
import { RestaurantClient } from "../api-client";

export interface IRestaurantContext {
  authUser: CognitoUser | any;
  fetchRestaurantByAdminSub: () => Promise<void>;
  loading: boolean;
  restaurant: Restaurant | null;
  setRestaurant: (restaurant: Restaurant | null) => void;
  sub?: string;
  updateRestaurantStatus: (status: RestaurantStatus) => void;
  updateRestaurantLoggingStatus: (
    loggingEnabled: Restaurant["loggingEnabled"]
  ) => void;
  updateRestaurantBillingDetails: (
    billingDetails: Restaurant["billingDetails"]
  ) => void;
}

const RestaurantContext: React.Context<{}> = createContext<
  IRestaurantContext | {}
>({});

const RestaurantContextProvider = ({
  children,
}: {
  children: ReactElement;
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [authUser, setAuthUser] = useState<CognitoUser | any>();
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);

  const sub =
    typeof authUser?.attributes?.sub === "string"
      ? authUser.attributes.sub
      : undefined;

  const sleep = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const fetchRestaurantByAdminSub = React.useCallback(async () => {
    setLoading(true);

    try {
      // NOTE:
      // `RestaurantClient.queryByAdminSub()` gibt bei API/Auth/Netzwerk-Problemen
      // (indirekt via graphqlRequest) ebenfalls `null` zurück.
      // Ohne Retry würden wir dann fälschlich "Kein Restaurant gefunden" anzeigen.
      //
      // Strategie (sparsam, aber robust):
      // 1) Cognito `sub` (Restaurant.adminSub) → 2) kurzer Retry → 3) einmal bypassCache refresh
      //
      // Aus dem Schema: `Restaurant.adminSub: String` ist das Feld für die Cognito-Sub-Zuordnung.
      const adminSub =
        typeof authUser?.attributes?.sub === "string"
          ? authUser.attributes.sub
          : null;

      if (!adminSub) {
        return;
      }

      let _restaurant: Restaurant | null = null;

      // 1) First pass (Cognito sub)
      _restaurant = await RestaurantClient.queryByAdminSub(adminSub);

      // 2) Retry once (transient hiccups)
      if (!_restaurant) {
        await sleep(400);
        _restaurant = await RestaurantClient.queryByAdminSub(adminSub);
      }

      // 3) Refresh Auth cache once, then try again
      if (!_restaurant) {
        try {
          const freshUser = await Auth.currentAuthenticatedUser({
            bypassCache: true,
          });
          setAuthUser(freshUser);

          const freshSub =
            typeof freshUser?.attributes?.sub === "string"
              ? freshUser.attributes.sub
              : null;
          if (freshSub) {
            _restaurant = await RestaurantClient.queryByAdminSub(freshSub);
          }
        } catch (_refreshError) {
          // ignore – falls refresh fehlschlägt, bleibt es bei "nicht gefunden"
        }
      }

      if (_restaurant) {
        setRestaurant(_restaurant);
      } else {
        // Kein Restaurant gefunden -> keine automatische Heuristik. Admin-Zuordnung ist Source of Truth.
        message.warning(
          "Du bist eingeloggt, aber deinem Account ist kein Restaurant zugeordnet."
        );
      }
    } catch (error) {
      console.error("Error fetching restaurant:", error);
    } finally {
      setLoading(false);
    }
  }, [authUser?.attributes?.sub]);

  async function updateRestaurantBillingDetails(
    billingDetails: Restaurant["billingDetails"]
  ) {
    if (!restaurant?.id) {
      return message.warning("Keine Restaurant ID gefunden...");
    }

    const updatedRestaurant = await RestaurantClient.updateItem({
      id: restaurant.id,
      billingDetails,
    });

    if (!updatedRestaurant) {
      return message.error("Restaurant konnte nicht aktualisiert werden");
    }

    setRestaurant(updatedRestaurant);

    message.success("Rechnungsanschrift aktualisiert");
  }

  async function updateRestaurantStatus(status: RestaurantStatus) {
    if (!restaurant?.id) {
      return message.warning("Keine Restaurant ID gefunden...");
    }

    const updatedRestaurant = await RestaurantClient.updateItem({
      id: restaurant.id,
      status,
    });

    if (!updatedRestaurant) {
      return message.error("Restaurant konnte nicht aktualisiert werden");
    }

    setRestaurant(updatedRestaurant);

    message.success("Der Status des Restaurants ist nun " + status);
  }

  async function updateRestaurantLoggingStatus(
    loggingEnabled: Restaurant["loggingEnabled"]
  ) {
    if (!restaurant?.id) {
      return message.warning("Keine Restaurant ID gefunden...");
    }

    const updatedRestaurant = await RestaurantClient.updateItem({
      id: restaurant.id,
      loggingEnabled,
    });

    if (!updatedRestaurant) {
      return message.error("Restaurant konnte nicht aktualisiert werden");
    }

    setRestaurant(updatedRestaurant);

    message.success(
      "Logging ist nun " +
        (updatedRestaurant.loggingEnabled ? "aktiv" : "inaktiv")
    );
  }

  useEffect(() => {
    Auth.currentAuthenticatedUser().then(setAuthUser);
  }, []);

  useEffect(() => {
    if (!sub) {
      return;
    }
    fetchRestaurantByAdminSub();
  }, [fetchRestaurantByAdminSub, sub]);

  return (
    <RestaurantContext.Provider
      value={{
        authUser,
        fetchRestaurantByAdminSub,
        loading,
        restaurant,
        setRestaurant,
        sub,
        updateRestaurantStatus,
        updateRestaurantLoggingStatus,
        updateRestaurantBillingDetails,
      }}
    >
      {children}
    </RestaurantContext.Provider>
  );
};

export const useRestaurantContext = (): IRestaurantContext =>
  React.useContext(RestaurantContext) as IRestaurantContext;

export { RestaurantContext, RestaurantContextProvider };
